/**
 * API Client para mesclar freelancers duplicados.
 *
 * Endpoints expostos pelo backend (app/modules/freelancers/routes.py):
 *   GET  /freelancers/admin/merge/preview?old_id=X&new_id=Y
 *   POST /freelancers/admin/merge   { old_id, new_id }
 *
 * Ambos exigem permissão `mesclar_freelancers` no perfil de acesso.
 */

import { SUPABASE_URL } from "@/integrations/supabase/client";
import { apiFetch } from "@/lib/api-fetch";

const API_BASE_URL = SUPABASE_URL.replace(/\/$/, "");

function fetchWithAuth<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  return apiFetch<T>(`${API_BASE_URL}${endpoint}`, options);
}

// ============================================
// Tipos
// ============================================

export interface MergeFreelancerSnapshot {
  id: string;
  nome: string | null;
  email: string | null;
  telefone?: string | null;
  cidade?: string | null;
  pretensao_salarial?: string | null;
  areas?: string[] | null;
  linkedin_url?: string | null;
  portfolio_url?: string | null;
  is_active?: boolean | null;
  created_at?: string | null;
  updated_at?: string | null;
  [k: string]: unknown;
}

export interface MergePreview {
  old: MergeFreelancerSnapshot;
  new: MergeFreelancerSnapshot;
  /** Map "<table>.<column>": número de linhas que serão movidas/processadas. */
  counts: Record<string, number>;
  /** Map "<table>.<column>": número de linhas do OLD descartadas por conflito UNIQUE com NEW. */
  conflicts: Record<string, number>;
}

export interface MergeResult {
  merge_id: string;
  counts: Record<string, number>;
}

// ============================================
// API Calls
// ============================================

export async function previewMerge(oldId: string, newId: string): Promise<MergePreview> {
  const params = new URLSearchParams({ old_id: oldId, new_id: newId });
  return fetchWithAuth<MergePreview>(`/freelancers/admin/merge/preview?${params.toString()}`);
}

export async function executeMerge(oldId: string, newId: string): Promise<MergeResult> {
  return fetchWithAuth<MergeResult>(`/freelancers/admin/merge`, {
    method: "POST",
    body: JSON.stringify({ old_id: oldId, new_id: newId }),
  });
}

// ============================================
// Helpers de exibição
// ============================================

const TABLE_LABELS: Record<string, string> = {
  "activities.freelancer_id": "Atividades",
  "agent_evaluation_embeddings.freelancer_id": "Embeddings de avaliação",
  "agent_freelancer_embeddings.freelancer_id": "Embedding do freelancer",
  "agent_portfolio_analyses.freelancer_id": "Análises de portfolio",
  "candidaturas.freelancer_id": "Candidaturas",
  "controle_projetos.freelancer_id": "Projetos",
  "evaluation_group_freelancers.freelancer_id": "Grupos de avaliação",
  "freelancer_areas.freelancer_id": "Áreas",
  "freelancer_avaliacoes.freelancer_id": "Avaliações de perfil",
  "freelancer_cadastro_historico.freelancer_id": "Histórico de cadastro",
  "freelancer_sinais_reputacao.freelancer_id": "Sinais de reputação",
  "freelancer_skills_processed.freelancer_id": "Skills processadas",
  "freelancer_status_historico.freelancer_id": "Histórico de status",
  "projetos.freelancer_id": "Histórico de projetos",
  "recommendation_details.freelancer_id": "Detalhes de recomendações",
  "recommendation_feedback.freelancer_id": "Feedback de recomendações",
  "vaga_recommendations.freelancer_id": "Recomendações por vaga",
  "vagas_kanban_posicoes.freelancer_id": "Posições no Kanban",
  "vagas.freelancer_recomendado": "Vagas onde é recomendado",
  "vagas.freelancers_selecionados": "Vagas onde está selecionado",
  "vagas.freelancers_ocultados": "Vagas onde está ocultado",
  "availability_checks.freelancer_id": "Consultas de disponibilidade",
  "availability_signals.freelancer_id": "Sinais de disponibilidade",
  "freelancer_agencia_indicacoes.freelancer_id": "Indicações de agência",
  "freelancer_auto_tags.freelancer_id": "Tags automáticas",
  "freelancer_campo_historico.freelancer_id": "Histórico de campos",
  "freelancer_events.freelancer_id": "Linha do tempo",
  "freelancer_experiencias.freelancer_id": "Experiências",
  "freelancer_linkedin_history.freelancer_id": "Histórico do LinkedIn",
  "freelancer_observacoes.freelancer_id": "Observações",
  "freelancer_skills.freelancer_id": "Skills",
  "freelancer_visualizacoes.freelancer_id": "Visualizações do perfil",
  "invites.freelancer_id": "Convites de proposta",
  "linkedin_extraction_jobs.freelancer_id": "Importações do LinkedIn",
  "omie_clientes.freelancer_id": "Cadastro no Omie",
  "omie_os.freelancer_id": "Ordens de serviço do Omie",
  "portfolio_analysis_status.freelancer_id": "Análise de portfolio",
  "shortlist_escolhas.freelancer_id": "Escolhas de shortlist",
  "tag_taxonomy_suggestions.freelancer_id": "Sugestões de tag",
  "vaga_divergencias.freelancer_id": "Divergências em vagas",
  "vaga_envio_candidatos.freelancer_id": "Envios de candidatos",
  "vaga_provas_abertas.freelancer_id": "Provas em vagas",
  "whatsapp_conversations.freelancer_id": "Conversas de WhatsApp",
  "whatsapp_extraction_items.freelancer_id": "Itens extraídos do WhatsApp",
};

export function labelForFkKey(key: string): string {
  return TABLE_LABELS[key] ?? key;
}
